import { useLocation } from "@entry/navigation";
import { isValidElement, useEffect } from "react";

import { Button } from "@components/Button";
import { ButtonType } from "@components/Button/types";
import { OnboardingHeader } from "@components/Header";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Body, H2 } from "@components/Typography";
import { useIsInEntryShell } from "@entry/EntryShellContext";
import { EntryStepLayout } from "@entry/EntryStepLayout";
import { Analytics } from "@services";
import { getRouteForPath } from "@utils";
import { Container, Form, Heading, useStyles } from "./styles";
import { BottomButtonProps, OnboardingLayoutProps } from "./types";

// Shared components (AddressForm, ErrorComponent) render this from both
// inside and outside the entry shell, so it has to stay context-aware.
// Everything else about rendering — including analytics — belongs to
// whichever variant actually renders, not to this switch itself.
export const OnboardingLayout = (props: OnboardingLayoutProps) => {
  const inEntryShell = useIsInEntryShell();

  if (inEntryShell) {
    return <EntryStepLayout {...props} />;
  }

  return <ClassicOnboardingLayout {...props} />;
};

const ClassicOnboardingLayout = ({
  title,
  subtitle,
  subtitleStyle,
  cta: ctaProp,
  footer,
  children,
  style,
  analytics = true,
  analyticsName,
  subContent,
  hideLogout = false,
  footerStyle,
}: OnboardingLayoutProps) => {
  const styles = useStyles();

  const location = useLocation();

  const route = analyticsName ?? getRouteForPath(location.pathname);

  const logPrimaryCtaAnalytic = () => {
    if (!analytics) return;
    Analytics.track(`${route} Primary CTA Pressed`);
  };

  const logSecondaryCtaAnalytic = () => {
    if (!analytics) return;
    Analytics.track(`${route} Secondary CTA Pressed`);
  };

  useEffect(() => {
    if (!analytics) return;

    Analytics.page(route);
  }, [analytics]);

  const cta = (() => {
    if (!ctaProp) return null;
    if (isValidElement(ctaProp)) return ctaProp;

    const { primary, secondary } = ctaProp as BottomButtonProps;

    if (!primary && !secondary) return null;

    const primaryCtaPress = () => {
      logPrimaryCtaAnalytic();
      primary?.onClick?.();
    };

    const secondaryCtaPress = () => {
      logSecondaryCtaAnalytic();
      secondary?.onClick?.();
    };

    return (
      <div>
        {primary ? <Button {...primary} onClick={primaryCtaPress} /> : null}

        {secondary ? (
          <>
            <Spacing size={SpacingSizes.XS} />
            <Button type={ButtonType.Secondary} {...secondary} onClick={secondaryCtaPress} />
          </>
        ) : null}

        <Spacing size={footer ? SpacingSizes.M : SpacingSizes.XL} />
      </div>
    );
  })();

  return (
    <>
      <OnboardingHeader hideLogout={hideLogout} />
      <Container style={styles.container}>
        <Form autoComplete="off" style={{ ...styles.form, ...style }}>
          {title || subtitle ? (
            <Heading>
              {title ? <H2>{title}</H2> : null}
              {subtitle ? (
                <>
                  <Spacing size={SpacingSizes.M} />
                  <Body style={subtitleStyle}>{subtitle}</Body>
                </>
              ) : null}
            </Heading>
          ) : null}

          {children}

          <div style={footerStyle}>
            {cta}
            {footer}
          </div>
        </Form>
        {subContent}
      </Container>
    </>
  );
};
